import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AccountCircle } from '@mui/icons-material';
import { Sheet }  from '@mui/joy';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography
} from '@mui/material';

// ヘッダーに渡されたpropsの型
interface HeaderProps {
  userId: string;
  setUserId: React.Dispatch<React.SetStateAction<string>>;
  userName: string;
  setUserName: React.Dispatch<React.SetStateAction<string>>;
  pictureUrl: string;
  setPictureUrl: React.Dispatch<React.SetStateAction<string>>;
}

export const Header: React.FC<HeaderProps> = ({ userId, setUserId, userName, setUserName, pictureUrl, setPictureUrl }) => {
  const [openDialog, setOpenDialog] = useState(false);
  const [isProfileIconPressed, setIsProfileIconPressed] = useState(false);

  const navigate = useNavigate();

  // ログアウトの処理
  const handleLogout = () => {
    navigate("/login");
    setUserId("");
    setUserName("");
    setPictureUrl("");
    setOpenDialog(false);
  };

  /**
   * タイトルをクリックした時の一覧へ遷移する動作
   */
  const handleEventsNavigation = async () => {
    if (userId === null || userId === "") {
      return;
    }
    const params = {
      user_id: userId
    };
    navigate("/portal", { state: params });
  };

  return (
    <>
      {/* ヘッダ */}
      <Sheet
        variant="solid"
        invertedColors
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '10px 20px',
          backgroundColor: '#ff9800', // 背景色
          position: 'fixed', // 画面上部に固定
          zIndex: 1000,
          top: 0,  // スクロールしても上部に固定
          left: 0,  // 左端から固定
          width: '100%', // 横幅を100%に設定
          boxSizing: 'border-box', // 内側のパディングを考慮して幅を設定
          overflow: 'hidden', // 右端が切れないようにする
          height: '10vh',
          borderBottom: '1px solid #fff', // ヘッダ下部のボーダー
          // スマホ用にパディングや高さの調整
          '@media (max-width: 600px)': {
            height: '8vh',
            padding: '5px 10px',
          }
        }}
      >
        {/* 左側のタイトル部分 */}
        <Box sx={{ flex: 1 }}>
          <Typography
            onClick={handleEventsNavigation}
            sx={{
              fontSize: { xs: '14px', sm: '16px' },
              color: 'white',
              fontWeight: 'bold',
              letterSpacing: '2px', // 少し広めに
              padding: { xs: '8px', sm: '12px' },
              cursor: 'pointer',
              background: '#ff9800',
              borderRadius: '8px', // 丸みを持たせる
              textAlign: 'center',
              display: 'inline-block',
              transition: 'all 0.3s ease',
              position: 'relative',
              '&:hover': {
                backgroundColor: '#e68900',
                boxShadow: '0 6px 12px rgba(0, 0, 0, 0.2)', // シャドウ効果を強化
                transform: 'scale(1.1)', // 少し大きくなる
              },
              '&:active': {
                transform: 'scale(0.95)',
                boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)', // 押下時の影を小さく
              },
            }}
          >
            TKG BOOKING SYSTEM
          </Typography>
        </Box>

        {/* 右側のユーザー名部分 */}
        <Box sx={{ display: 'flex', flexShrink: 0, alignItems: 'center' }}>
          {/* ユーザー名とアイコン */}
          <Typography
            sx={{
              fontSize: { xs: '14px', sm: '16px' }, // スマホ用とデスクトップ用のフォントサイズ
              color: 'white',
              fontWeight: 'normal',
              marginRight: 2,
            }}
          >
            {userName}
          </Typography>
          {pictureUrl ? 
            <img
              src={pictureUrl}
              alt='User Profile'
              onClick={() => setOpenDialog(true)}
              onMouseDown={() => setIsProfileIconPressed(true)}
              onMouseUp={() => setIsProfileIconPressed(false)}
              onMouseLeave={() => setIsProfileIconPressed(false)}
              style={{
                width: '40px',
                height: '40px',
                borderRadius: '50%',
                border: '2px solid white',
                cursor: 'pointer',
                transform: isProfileIconPressed ? 'scale(0.9)' : 'scale(1)',
                transition: 'transform 0.1s ease',
              }}
            />
          : 
            <AccountCircle 
              onClick={() => setOpenDialog(true)}
              onMouseDown={() => setIsProfileIconPressed(true)}
              onMouseUp={() => setIsProfileIconPressed(false)}
              onMouseLeave={() => setIsProfileIconPressed(false)}
              style={{
                width: '40px',
                height: '40px',
                borderRadius: '50%',
                border: '2px solid white',
                cursor: 'pointer',
                transform: isProfileIconPressed ? 'scale(0.9)' : 'scale(1)',
                transition: 'transform 0.1s ease',
              }}
              sx={{ color: 'white', fontSize: '1.5rem' }}
            />
          }
        </Box>
      </Sheet>

      {/* ユーザーダイアログ */}
      <Dialog open={openDialog} onClick={() => setOpenDialog(false)}>
        <DialogTitle sx={{ fontSize: 20, fontWeight: 'bold', textAlign: 'center', position: 'relative' }}>
          {/* 右上の「閉じる」ボタン */}
          <Button
            onClick={() => setOpenDialog(false)}
            sx={{
              position: 'absolute',
              top: '8px',
              right: '8px',
              minWidth: '30px',
              minHeight: '30px',
              padding: '0',
              fontSize: '16px',
              lineHeight: 1,
            }}
          >
            ×
          </Button>
        </DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '20px' }}>
          {pictureUrl ? 
            <img
              src={pictureUrl}
              alt='User Profile'
              style={{
                width: '80px',
                height: '80px',
                borderRadius: '50%',
                border: '4px solid #ff9800',
                marginBottom: '20px',
              }}
            />
          : 
            <AccountCircle 
              style={{
                width: '80px',
                height: '80px',
                borderRadius: '50%',
                border: '4px solid #ff9800',
                marginBottom: '20px',
              }}
              sx={{ color: '#ff9800', fontSize: '1.5rem' }}
            />
          }
          <Typography sx={{ fontSize: 18, fontWeight: 'normal', color: '#616161' }}>
            {userName}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center' }}>
          <Button variant="contained" color="error" onClick={handleLogout}>ログアウトする</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
